export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    public errors: Record<string, string[]> = {},
    public retryAfterSeconds: number | null = null,
  ) {
    super(message);
  }
}
export async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`/api/v1${path}`, {
    ...init,
    credentials: 'same-origin',
    headers: { ...init?.headers },
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new ApiError(
      response.status,
      error.code ?? 'request_failed',
      error.title ?? 'The request failed. Please try again.',
      error.errors ?? {},
      parseRetryAfter(response.headers.get('Retry-After')),
    );
  }
  return response.status === 204 ? (undefined as T) : response.json();
}

function parseRetryAfter(value: string | null): number | null {
  if (!value) return null;
  const seconds = Number(value);
  if (Number.isFinite(seconds) && seconds >= 0) return Math.ceil(seconds);

  const retryAt = Date.parse(value);
  return Number.isNaN(retryAt) ? null : Math.max(0, Math.ceil((retryAt - Date.now()) / 1000));
}

export async function mutation<T>(path: string, body?: unknown): Promise<T> {
  return apiMutation<T>(path, 'POST', body);
}

export async function apiMutation<T>(
  path: string,
  method: 'POST' | 'PUT' | 'PATCH' | 'DELETE',
  body?: unknown,
  signal?: AbortSignal,
) {
  // A fresh token reflects the current authenticated identity after login/logout.
  const csrf = await api<{ requestToken: string }>('/auth/csrf', { signal });
  return api<T>(path, {
    method,
    signal,
    headers: { 'Content-Type': 'application/json', 'X-CSRF-TOKEN': csrf.requestToken },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
}
